import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Upload, FileText, FileType2, Loader2, CheckCircle2, TrendingDown, Download, History, RotateCcw, Mail, Eye } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import WeKitLogo from "@/components/WeKitLogo";

type Format = "a4" | "letter" | "pptx";

interface AssetRow {
  format: Format;
  storage_path: string;
  public_url: string;
  filename: string | null;
  updated_at: string;
}

interface VersionRow {
  id: string;
  format: Format;
  storage_path: string;
  public_url: string;
  filename: string | null;
  size_bytes: number | null;
  created_at: string;
}

interface LeadRow {
  id: string;
  parent_name: string;
  email: string;
  mobile: string | null;
  child_age_range: string | null;
  source: string | null;
  created_at: string;
}

const FORMAT_META: Record<Format, { label: string; ext: string; accept: string; icon: typeof FileText }> = {
  a4:     { label: "A4 PDF",       ext: "pdf",  accept: "application/pdf", icon: FileText },
  letter: { label: "US Letter PDF",ext: "pdf",  accept: "application/pdf", icon: FileText },
  pptx:   { label: "PowerPoint",   ext: "pptx", accept: "application/vnd.openxmlformats-officedocument.presentationml.presentation", icon: FileType2 },
};

const WINDOWS = [
  { value: "7",  label: "Last 7 days"  },
  { value: "30", label: "Last 30 days" },
  { value: "90", label: "Last 90 days" },
];

const SIGNED_URL_TTL = 60 * 60 * 24 * 365 * 10;

function formatBytes(n: number | null): string {
  if (!n) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

function csvEscape(v: string | number): string {
  const s = String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export default function AdminParentGuide() {
  const { toast } = useToast();
  const [assets, setAssets] = useState<Record<Format, AssetRow | null>>({ a4: null, letter: null, pptx: null });
  const [versions, setVersions] = useState<Record<Format, VersionRow[]>>({ a4: [], letter: [], pptx: [] });
  const [uploading, setUploading] = useState<Format | null>(null);
  const [rollingBack, setRollingBack] = useState<string | null>(null);
  const [historyOpen, setHistoryOpen] = useState<Record<Format, boolean>>({ a4: false, letter: false, pptx: false });
  const inputRefs = useRef<Record<Format, HTMLInputElement | null>>({ a4: null, letter: null, pptx: null });

  const [days, setDays] = useState("30");
  const [funnelLoading, setFunnelLoading] = useState(true);
  const [funnel, setFunnel] = useState<{ views: number; previews: number; downloads: number; submissions: number; a4: number; letter: number; pptx: number } | null>(null);

  const [leads, setLeads] = useState<LeadRow[]>([]);
  const [leadsLoading, setLeadsLoading] = useState(true);
  const [resending, setResending] = useState<string | null>(null);

  const loadAssets = async () => {
    const { data } = await supabase.from("parent_guide_assets").select("format,storage_path,public_url,filename,updated_at");
    const next: Record<Format, AssetRow | null> = { a4: null, letter: null, pptx: null };
    for (const r of (data ?? []) as AssetRow[]) next[r.format] = r;
    setAssets(next);
  };

  const loadVersions = async () => {
    const { data } = await supabase
      .from("parent_guide_asset_versions")
      .select("id,format,storage_path,public_url,filename,size_bytes,created_at")
      .order("created_at", { ascending: false })
      .limit(200);
    const next: Record<Format, VersionRow[]> = { a4: [], letter: [], pptx: [] };
    for (const r of (data ?? []) as VersionRow[]) next[r.format]?.push(r);
    setVersions(next);
  };

  const loadLeads = async () => {
    setLeadsLoading(true);
    const { data } = await supabase
      .from("parent_guide_leads")
      .select("id,parent_name,email,mobile,child_age_range,source,created_at")
      .order("created_at", { ascending: false })
      .limit(50);
    setLeads((data ?? []) as LeadRow[]);
    setLeadsLoading(false);
  };

  useEffect(() => { void loadAssets(); void loadVersions(); void loadLeads(); }, []);

  useEffect(() => {
    let cancelled = false;
    setFunnelLoading(true);
    const since = new Date(Date.now() - parseInt(days, 10) * 24 * 60 * 60 * 1000).toISOString();
    (async () => {
      const { data, error } = await supabase
        .from("analytics_events")
        .select("event, props, session_id, created_at")
        .eq("surface", "parent-guide")
        .gte("created_at", since)
        .limit(20000);
      if (cancelled) return;
      if (error) { setFunnel(null); setFunnelLoading(false); return; }
      const rows = data ?? [];
      const uniq = (predicate: (r: typeof rows[number]) => boolean) => {
        const set = new Set<string>();
        for (const r of rows) if (predicate(r)) set.add(r.session_id ?? "anon");
        return set.size;
      };
      const views       = uniq((r) => r.event === "parent_guide_view");
      const previews    = uniq((r) => r.event === "parent_guide_preview_click");
      const downloads   = uniq((r) => r.event === "parent_guide_download");
      const submissions = uniq((r) => r.event === "parent_guide_form_success" || r.event === "parent_guide_form_submit");
      const a4     = rows.filter((r) => r.event === "parent_guide_download" && (r.props as any)?.format === "a4_pdf").length;
      const letter = rows.filter((r) => r.event === "parent_guide_download" && (r.props as any)?.format === "letter_pdf").length;
      const pptx   = rows.filter((r) => r.event === "parent_guide_download" && (r.props as any)?.format === "pptx").length;
      setFunnel({ views, previews, downloads, submissions, a4, letter, pptx });
      setFunnelLoading(false);
    })();
    return () => { cancelled = true; };
  }, [days]);

  const steps = useMemo(() => {
    if (!funnel) return [] as { name: string; users: number }[];
    return [
      { name: "Page viewed",     users: funnel.views },
      { name: "Preview clicked", users: funnel.previews },
      { name: "PDF downloaded",  users: funnel.downloads },
      { name: "Form submitted",  users: funnel.submissions },
    ];
  }, [funnel]);

  const topOfFunnel = steps[0]?.users ?? 0;

  const handleUpload = async (format: Format, file: File) => {
    setUploading(format);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const ext = FORMAT_META[format].ext;
      const path = `${format}/v-${Date.now()}.${ext}`;
      const { error: upErr } = await supabase.storage.from("parent-guide").upload(path, file, {
        cacheControl: "3600", upsert: true, contentType: file.type,
      });
      if (upErr) throw upErr;
      const { data: signed, error: signErr } = await supabase.storage
        .from("parent-guide").createSignedUrl(path, SIGNED_URL_TTL);
      if (signErr || !signed?.signedUrl) throw signErr ?? new Error("could not sign URL");

      // Record version history
      const { error: verErr } = await supabase.from("parent_guide_asset_versions").insert({
        format, storage_path: path, public_url: signed.signedUrl,
        filename: file.name, size_bytes: file.size, uploaded_by: user?.id ?? null,
      });
      if (verErr) throw verErr;

      // Point live asset at the new upload
      const { error: assetErr } = await supabase.from("parent_guide_assets").upsert({
        format, storage_path: path, public_url: signed.signedUrl,
        filename: file.name, uploaded_by: user?.id ?? null,
        updated_at: new Date().toISOString(),
      });
      if (assetErr) throw assetErr;

      toast({ title: `${FORMAT_META[format].label} updated`, description: "Parents will now download this file." });
      await Promise.all([loadAssets(), loadVersions()]);
    } catch (e: any) {
      toast({ title: "Upload failed", description: e?.message ?? "Please try again.", variant: "destructive" });
    } finally {
      setUploading(null);
    }
  };

  const handleRollback = async (v: VersionRow) => {
    setRollingBack(v.id);
    try {
      // Re-sign (URLs may have expired or bucket key rotated)
      const { data: signed, error: signErr } = await supabase.storage
        .from("parent-guide").createSignedUrl(v.storage_path, SIGNED_URL_TTL);
      if (signErr || !signed?.signedUrl) throw signErr ?? new Error("could not sign URL");

      const { data: { user } } = await supabase.auth.getUser();
      const { error: assetErr } = await supabase.from("parent_guide_assets").upsert({
        format: v.format, storage_path: v.storage_path, public_url: signed.signedUrl,
        filename: v.filename, uploaded_by: user?.id ?? null,
        updated_at: new Date().toISOString(),
      });
      if (assetErr) throw assetErr;
      toast({ title: "Rolled back", description: `${FORMAT_META[v.format].label} restored to ${v.filename ?? "earlier version"}.` });
      await loadAssets();
    } catch (e: any) {
      toast({ title: "Rollback failed", description: e?.message ?? "Please try again.", variant: "destructive" });
    } finally {
      setRollingBack(null);
    }
  };

  const handleExportCsv = () => {
    if (!funnel) return;
    const lines: string[] = [];
    lines.push(["step", "users", "pct_of_top", "drop_vs_prev"].join(","));
    steps.forEach((s, i) => {
      const prev = i === 0 ? s.users : steps[i - 1].users;
      const drop = i === 0 ? "" : (prev === 0 ? "0" : String(Math.max(0, Math.round(((prev - s.users) / prev) * 100))));
      const conv = topOfFunnel === 0 ? 0 : Math.round((s.users / topOfFunnel) * 100);
      lines.push([csvEscape(s.name), s.users, conv, drop].join(","));
    });
    lines.push("");
    lines.push(["format", "downloads"].join(","));
    lines.push(["a4_pdf", funnel.a4].join(","));
    lines.push(["letter_pdf", funnel.letter].join(","));
    lines.push(["pptx", funnel.pptx].join(","));

    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, "");
    a.href = url;
    a.download = `parent-guide-funnel-last-${days}d-${stamp}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  const handleResend = async (lead: LeadRow) => {
    setResending(lead.id);
    try {
      const { error } = await supabase.functions.invoke("parent-guide-resend-confirmation", {
        body: { lead_id: lead.id },
      });
      if (error) throw error;
      toast({ title: "Confirmation resent", description: `Email queued for ${lead.email}.` });
    } catch (e: any) {
      toast({ title: "Resend failed", description: e?.message ?? "Please try again.", variant: "destructive" });
    } finally {
      setResending(null);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-30 border-b border-border/60 bg-background/85 backdrop-blur">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Button asChild variant="ghost" size="sm" className="gap-1.5 -ml-2">
              <Link to="/admin"><ArrowLeft className="h-4 w-4" /> Admin</Link>
            </Button>
            <div className="hidden sm:block h-5 w-px bg-border" />
            <Link to="/" className="hidden sm:inline-flex"><WeKitLogo size="sm" /></Link>
          </div>
          <Button asChild variant="outline" size="sm">
            <Link to="/parent-guide" target="_blank">Open parent page ↗</Link>
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-10 space-y-10">
        <section>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Parent Guide</h1>
          <p className="text-muted-foreground mt-2 max-w-2xl">
            Upload, preview and manage versions of the parent-facing PDF / PowerPoint downloads.
          </p>
        </section>

        {/* Upload + preview + version history */}
        <section className="grid gap-4 md:grid-cols-3">
          {(Object.keys(FORMAT_META) as Format[]).map((fmt) => {
            const meta = FORMAT_META[fmt];
            const Icon = meta.icon;
            const current = assets[fmt];
            const vs = versions[fmt];
            const historyShown = historyOpen[fmt];
            return (
              <Card key={fmt} className="flex flex-col">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-base font-semibold">
                    <Icon className="h-4 w-4" /> {meta.label}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {current ? (
                    <div className="rounded-lg border border-border/60 bg-muted/30 p-3 text-xs space-y-1">
                      <div className="flex items-center gap-1.5 text-emerald-600 font-medium">
                        <CheckCircle2 className="h-3.5 w-3.5" /> Live version
                      </div>
                      <div className="truncate text-foreground font-medium">{current.filename ?? "(no name)"}</div>
                      <div className="text-muted-foreground">Updated {new Date(current.updated_at).toLocaleString()}</div>
                    </div>
                  ) : (
                    <p className="text-xs text-muted-foreground">
                      No custom upload yet — parents see the built-in file shipped with the site.
                    </p>
                  )}

                  {/* Preview */}
                  {current && (
                    <div className="rounded-lg border border-border/60 overflow-hidden bg-muted/10">
                      {fmt === "pptx" ? (
                        <div className="p-4 text-xs text-muted-foreground space-y-2">
                          <div className="flex items-center gap-1.5 text-foreground font-medium">
                            <Eye className="h-3.5 w-3.5" /> Preview
                          </div>
                          <p>PowerPoint files can't preview in the browser. Download to review before publishing.</p>
                          <Button asChild size="sm" variant="outline" className="w-full">
                            <a href={current.public_url} target="_blank" rel="noreferrer">
                              <Download className="mr-1.5 h-3.5 w-3.5" /> Download to preview
                            </a>
                          </Button>
                        </div>
                      ) : (
                        <>
                          <iframe
                            src={current.public_url}
                            title={`${meta.label} preview`}
                            className="w-full h-[360px] bg-white"
                          />
                          <div className="p-2 text-right">
                            <a href={current.public_url} target="_blank" rel="noreferrer" className="text-xs text-primary underline">
                              Open full-size ↗
                            </a>
                          </div>
                        </>
                      )}
                    </div>
                  )}

                  <input
                    ref={(el) => { inputRefs.current[fmt] = el; }}
                    type="file"
                    accept={meta.accept}
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) void handleUpload(fmt, f);
                      e.target.value = "";
                    }}
                  />
                  <Button
                    className="w-full"
                    disabled={uploading === fmt}
                    onClick={() => inputRefs.current[fmt]?.click()}
                  >
                    {uploading === fmt ? (
                      <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Uploading…</>
                    ) : (
                      <><Upload className="mr-2 h-4 w-4" /> Upload new {meta.label}</>
                    )}
                  </Button>

                  {/* Version history */}
                  <div>
                    <Button
                      variant="ghost" size="sm"
                      className="w-full justify-between text-xs h-8"
                      onClick={() => setHistoryOpen((s) => ({ ...s, [fmt]: !s[fmt] }))}
                    >
                      <span className="inline-flex items-center gap-1.5">
                        <History className="h-3.5 w-3.5" /> Version history ({vs.length})
                      </span>
                      <span className="text-muted-foreground">{historyShown ? "Hide" : "Show"}</span>
                    </Button>
                    {historyShown && (
                      <div className="mt-2 space-y-2 max-h-72 overflow-y-auto">
                        {vs.length === 0 ? (
                          <p className="text-xs text-muted-foreground px-1">No history yet.</p>
                        ) : vs.map((v) => {
                          const isLive = current?.storage_path === v.storage_path;
                          return (
                            <div key={v.id} className="rounded-md border border-border/60 p-2 text-xs space-y-1">
                              <div className="flex items-start justify-between gap-2">
                                <div className="min-w-0 flex-1">
                                  <div className="truncate font-medium text-foreground">{v.filename ?? "(no name)"}</div>
                                  <div className="text-muted-foreground text-[11px]">
                                    {new Date(v.created_at).toLocaleString()} · {formatBytes(v.size_bytes)}
                                  </div>
                                </div>
                                {isLive && (
                                  <span className="shrink-0 rounded-full bg-emerald-500/10 text-emerald-600 px-1.5 py-0.5 text-[10px] font-medium">Live</span>
                                )}
                              </div>
                              <div className="flex items-center gap-2 pt-1">
                                <a href={v.public_url} target="_blank" rel="noreferrer" className="text-primary underline text-[11px]">Preview</a>
                                <Button
                                  size="sm" variant="outline"
                                  className="h-6 px-2 text-[11px] ml-auto"
                                  disabled={isLive || rollingBack === v.id}
                                  onClick={() => handleRollback(v)}
                                >
                                  {rollingBack === v.id ? (
                                    <Loader2 className="h-3 w-3 animate-spin" />
                                  ) : (
                                    <><RotateCcw className="h-3 w-3 mr-1" /> Rollback</>
                                  )}
                                </Button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </section>

        {/* Funnel */}
        <section>
          <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
            <div>
              <h2 className="font-display text-2xl font-bold tracking-tight">Funnel</h2>
              <p className="text-sm text-muted-foreground">
                Unique sessions per step. Preview clicks → PDF downloads → form submissions.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={handleExportCsv} disabled={!funnel}>
                <Download className="h-4 w-4 mr-1.5" /> Export CSV
              </Button>
              <Select value={days} onValueChange={setDays}>
                <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {WINDOWS.map((w) => <SelectItem key={w.value} value={w.value}>{w.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <Card>
            <CardContent className="p-6">
              {funnelLoading ? (
                <div className="py-12 text-center text-muted-foreground text-sm">Loading…</div>
              ) : !funnel ? (
                <div className="py-12 text-center text-muted-foreground text-sm">No analytics yet.</div>
              ) : (
                <div className="space-y-6">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {steps.map((s, i) => {
                      const conv = topOfFunnel === 0 ? 0 : Math.round((s.users / topOfFunnel) * 100);
                      return (
                        <div key={s.name} className="rounded-xl border border-border/60 bg-card/60 p-4">
                          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Step {i + 1}</div>
                          <div className="font-display text-2xl font-bold text-foreground mt-1">{s.users.toLocaleString()}</div>
                          <div className="text-xs text-muted-foreground mt-1">{s.name}</div>
                          <div className="text-[11px] text-muted-foreground mt-2">{conv}% of top</div>
                        </div>
                      );
                    })}
                  </div>

                  <table className="w-full text-sm">
                    <thead className="text-[11px] uppercase tracking-wider text-muted-foreground">
                      <tr>
                        <th className="text-left font-semibold px-2 py-2">Step</th>
                        <th className="text-right font-semibold px-2 py-2">Users</th>
                        <th className="text-right font-semibold px-2 py-2">From top</th>
                        <th className="text-right font-semibold px-2 py-2">Drop vs prev</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border/60">
                      {steps.map((s, i) => {
                        const prev = i === 0 ? s.users : steps[i - 1].users;
                        const drop = prev === 0 ? 0 : Math.max(0, Math.round(((prev - s.users) / prev) * 100));
                        const conv = topOfFunnel === 0 ? 0 : Math.round((s.users / topOfFunnel) * 100);
                        return (
                          <tr key={s.name}>
                            <td className="px-2 py-2 font-medium">{i + 1}. {s.name}</td>
                            <td className="px-2 py-2 text-right tabular-nums">{s.users.toLocaleString()}</td>
                            <td className="px-2 py-2 text-right tabular-nums text-muted-foreground">{conv}%</td>
                            <td className={`px-2 py-2 text-right tabular-nums ${drop > 40 ? "text-destructive" : "text-muted-foreground"}`}>
                              {i === 0 ? "—" : (<span className="inline-flex items-center gap-1"><TrendingDown className="h-3 w-3" />{drop}%</span>)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>

                  <div className="rounded-xl border border-border/60 bg-muted/30 p-4">
                    <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-2">Download format split</div>
                    <div className="grid grid-cols-3 gap-3 text-sm">
                      <div><span className="font-semibold">{funnel.a4}</span> A4 PDF</div>
                      <div><span className="font-semibold">{funnel.letter}</span> Letter PDF</div>
                      <div><span className="font-semibold">{funnel.pptx}</span> PowerPoint</div>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </section>

        {/* Recent submissions */}
        <section>
          <div className="flex items-end justify-between gap-3 mb-4">
            <div>
              <h2 className="font-display text-2xl font-bold tracking-tight">Recent submissions</h2>
              <p className="text-sm text-muted-foreground">Last 50 parents who submitted the download form.</p>
            </div>
            <Button variant="ghost" size="sm" onClick={loadLeads} disabled={leadsLoading}>
              {leadsLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Refresh"}
            </Button>
          </div>
          <Card>
            <CardContent className="p-0 overflow-x-auto">
              {leadsLoading ? (
                <div className="py-12 text-center text-muted-foreground text-sm">Loading…</div>
              ) : leads.length === 0 ? (
                <div className="py-12 text-center text-muted-foreground text-sm">No submissions yet.</div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="text-[11px] uppercase tracking-wider text-muted-foreground bg-muted/30">
                    <tr>
                      <th className="text-left font-semibold px-4 py-2">Submitted</th>
                      <th className="text-left font-semibold px-4 py-2">Parent</th>
                      <th className="text-left font-semibold px-4 py-2">Email</th>
                      <th className="text-left font-semibold px-4 py-2">Child age</th>
                      <th className="text-left font-semibold px-4 py-2">Source</th>
                      <th className="text-right font-semibold px-4 py-2">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border/60">
                    {leads.map((l) => (
                      <tr key={l.id}>
                        <td className="px-4 py-2 whitespace-nowrap text-muted-foreground">{new Date(l.created_at).toLocaleString()}</td>
                        <td className="px-4 py-2 font-medium">{l.parent_name}</td>
                        <td className="px-4 py-2"><a href={`mailto:${l.email}`} className="text-primary underline">{l.email}</a></td>
                        <td className="px-4 py-2 text-muted-foreground">{l.child_age_range ?? "—"}</td>
                        <td className="px-4 py-2 text-muted-foreground">{l.source ?? "—"}</td>
                        <td className="px-4 py-2 text-right">
                          <Button
                            size="sm" variant="outline"
                            disabled={resending === l.id}
                            onClick={() => handleResend(l)}
                          >
                            {resending === l.id ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <><Mail className="h-3.5 w-3.5 mr-1.5" /> Resend</>
                            )}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>
        </section>
      </main>
    </div>
  );
}
